import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Building2,
  CreditCard,
  Shield,
  Clock,
  Globe,
  Check,
  X,
  Loader2,
  AlertCircle,
  Palette,
  Upload,
  ImageIcon,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  useTenantPricing,
  useCheckSubdomain,
  useCreateTenantSignup,
  type TenantPricingPlan,
} from '@/api/tenants';
import { LanguageSwitcher } from '@/components/language-switcher';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  previewBrandingColor,
  resetBrandingColors,
  DEFAULT_PRIMARY_COLOR,
} from '@/hooks/use-tenant-branding';
import { getCurrentTenantId } from '@/lib/tenant';

// Debounce hook for subdomain checking
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const signupSchema = z
  .object({
    businessName: z
      .string()
      .min(2, 'Business name must be at least 2 characters'),
    subdomain: z
      .string()
      .min(3, 'Subdomain must be at least 3 characters')
      .max(30, 'Subdomain must be at most 30 characters')
      .regex(
        /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
        'Subdomain must start and end with a letter or number, and can contain hyphens'
      )
      .refine(
        (val) =>
          ![
            'www',
            'api',
            'app',
            'admin',
            'dashboard',
            'mail',
            'smtp',
            'ftp',
          ].includes(val),
        'This subdomain is reserved'
      ),
    ownerName: z.string().min(2, 'Name must be at least 2 characters'),
    ownerEmail: z.string().email('Please enter a valid email address'),
    ownerPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
      ),
    confirmPassword: z.string(),
    gymCount: z.number().min(1, 'You need at least 1 gym'),
  })
  .refine((data) => data.ownerPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SignupFormData = z.infer<typeof signupSchema>;

export default function TenantSignupPage() {
  const tenantId = getCurrentTenantId();
  if (tenantId) {
    return <Navigate to="/register" replace />;
  }

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<TenantPricingPlan | null>(
    null
  );
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  // Branding state
  const [brandColor, setBrandColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [logoFile, setLogoFile] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // API hooks
  const { data: pricingData, isLoading: isPricingLoading } = useTenantPricing();
  const signupMutation = useCreateTenantSignup();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    trigger,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    mode: 'onChange',
    defaultValues: {
      gymCount: 1,
    },
  });

  const subdomain = watch('subdomain');
  const gymCount = watch('gymCount');
  const debouncedSubdomain = useDebounce(subdomain, 500);

  // Check subdomain availability
  const { data: subdomainCheck, isFetching: isSubdomainFetching } =
    useCheckSubdomain(
      debouncedSubdomain || '',
      Boolean(debouncedSubdomain && debouncedSubdomain.length >= 3)
    );

  useEffect(() => {
    if (subdomainCheck) {
      setSubdomainAvailable(subdomainCheck.available);
    }
  }, [subdomainCheck]);

  // Auto-select plan based on gym count
  useEffect(() => {
    if (pricingData?.plans && gymCount) {
      const matchingPlan = pricingData.plans.find(
        (plan) => gymCount <= plan.gymLimit
      );
      if (matchingPlan) {
        setSelectedPlan(matchingPlan);
      }
    }
  }, [gymCount, pricingData?.plans]);

  const totalPrice = selectedPlan
    ? gymCount * (pricingData?.pricePerGym || 100)
    : 0;

  // Handle logo upload
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (
      ![
        'image/png',
        'image/jpeg',
        'image/svg+xml',
        'image/webp',
        'image/gif',
      ].includes(file.type)
    ) {
      toast({
        title: t('tenantSignup.error'),
        description: t(
          'tenantSignup.invalidLogoType',
          'Please upload a PNG, JPG, SVG, WebP, or GIF file'
        ),
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: t('tenantSignup.error'),
        description: t(
          'tenantSignup.logoTooLarge',
          'File size must be less than 2MB'
        ),
        variant: 'destructive',
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setLogoFile(base64);
      setLogoPreview(base64);
    };
    reader.readAsDataURL(file);
  };

  // Handle brand color change
  const handleBrandColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    setBrandColor(color);
  };

  // Apply brand color preview whenever it changes
  useEffect(() => {
    previewBrandingColor(brandColor);
  }, [brandColor]);

  // Reset color preview when leaving the page
  useEffect(() => {
    return () => {
      resetBrandingColors();
    };
  }, []);

  // Remove logo
  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleContinueToPayment = async () => {
    const isStepValid = await trigger([
      'businessName',
      'subdomain',
      'ownerName',
      'ownerEmail',
      'ownerPassword',
      'confirmPassword',
    ]);

    if (!isStepValid) {
      return;
    }

    if (!subdomainAvailable) {
      toast({
        title: t('tenantSignup.error'),
        description: t('tenantSignup.subdomainNotAvailable'),
        variant: 'destructive',
      });
      return;
    }

    setStep(2);
  };

  const onSubmit = async (data: SignupFormData) => {
    setError(null);

    try {
      const result = await signupMutation.mutateAsync({
        tenant: {
          businessName: data.businessName,
          subdomain: data.subdomain,
          ownerEmail: data.ownerEmail,
          ownerName: data.ownerName,
          ownerPassword: data.ownerPassword,
          gymCount: data.gymCount,
          plan: selectedPlan?.id,
          color: brandColor,
          logo: logoFile || undefined,
        },
        successUrl: `${window.location.origin}/signup/success`,
        cancelUrl: `${window.location.origin}/signup`,
      });

      // Redirect to Stripe Checkout
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('tenantSignup.unexpectedError');
      setError(errorMessage);
      toast({
        title: t('tenantSignup.error'),
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-12">
      <div className="flex items-center">
        <div
          className={cn(
            'w-12 h-12 rounded-full flex items-center justify-center font-semibold text-lg',
            step >= 1
              ? 'text-background'
              : 'bg-muted text-muted-foreground'
          )}
          style={step >= 1 ? { backgroundColor: brandRed } : undefined}
        >
          1
        </div>
        <span className="ml-3 text-sm font-medium">
          {t('tenantSignup.steps.businessDetails')}
        </span>
      </div>
      <div className="w-20 h-0.5 mx-6 bg-muted relative">
        <div
          className={cn(
            'h-full transition-all absolute top-0 left-0',
            step >= 2 ? 'w-full' : 'w-0'
          )}
          style={{ backgroundColor: brandRed }}
        />
      </div>
      <div className="flex items-center">
        <div
          className={cn(
            'w-12 h-12 rounded-full flex items-center justify-center font-semibold text-lg',
            step >= 2
              ? 'text-background'
              : 'bg-muted text-muted-foreground'
          )}
          style={step >= 2 ? { backgroundColor: brandRed } : undefined}
        >
          2
        </div>
        <span className="ml-3 text-sm font-medium">
          {t('tenantSignup.steps.payment')}
        </span>
      </div>
    </div>
  );

  const renderPricingCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      {pricingData?.plans.map((plan) => (
        <Card
          key={plan.id}
          className={cn(
            'cursor-pointer transition-all hover:shadow-lg border-2',
            selectedPlan?.id === plan.id 
              ? 'border-[#C0392B]' 
              : 'hover:border-[#C0392B]/50'
          )}
          onClick={() => {
            setSelectedPlan(plan);
            setValue('gymCount', plan.gymLimit);
          }}
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-xl">{plan.name}</CardTitle>
            <CardDescription className="text-base">
              {t('tenantSignup.upToGyms', { count: plan.gymLimit })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold mb-4">
              €{plan.pricePerMonth}
              <span className="text-base font-normal text-muted-foreground">
                /{t('tenantSignup.month')}
              </span>
            </div>
            <ul className="space-y-2 text-sm">
              {plan.features?.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const brandRed = "#C0392B";

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="relative w-12 h-12">
                <img
                  src="/logo-with-text.png"
                  alt="THE NOT FIT Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-bold">THE NOT FIT</span>
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <Button variant="ghost" asChild>
                <Link to="/login">
                  {t('tenantSignup.signIn', 'Sign In')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('tenantSignup.title')}
          </h1>
          <p className="text-xl text-muted-foreground">
            {t('tenantSignup.subtitle')}
          </p>
        </div>

        {renderStepIndicator()}

        {/* Step 1: Business Details */}
        {step === 1 && (
          <div className="grid md:grid-cols-3 gap-8">
            {/* Main Form */}
            <Card className="md:col-span-2 border-2">
              <CardHeader>
                <CardTitle>{t('tenantSignup.businessDetails')}</CardTitle>
                <CardDescription>
                  {t('tenantSignup.businessDetailsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-4">
                  {/* Business Name */}
                  <div>
                    <Label htmlFor="businessName">
                      {t('tenantSignup.businessName')}
                    </Label>
                    <Input
                      id="businessName"
                      {...register('businessName')}
                      placeholder={t('tenantSignup.businessNamePlaceholder')}
                    />
                    {errors.businessName && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.businessName.message}
                      </p>
                    )}
                  </div>

                  {/* Subdomain */}
                  <div>
                    <Label htmlFor="subdomain">
                      {t('tenantSignup.subdomain')}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="subdomain"
                        {...register('subdomain')}
                        placeholder="yourgym"
                        className="flex-1"
                        onChange={(e) => {
                          const value = e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, '');
                          setValue('subdomain', value);
                          setSubdomainAvailable(null);
                        }}
                      />
                      <span className="text-muted-foreground">
                        .thenotfit.com
                      </span>
                      {isSubdomainFetching && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      {!isSubdomainFetching && subdomainAvailable === true && (
                        <Check className="h-5 w-5 text-green-500" />
                      )}
                      {!isSubdomainFetching && subdomainAvailable === false && (
                        <X className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    {errors.subdomain && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.subdomain.message}
                      </p>
                    )}
                    {subdomainAvailable === false && (
                      <p className="text-sm text-destructive mt-1">
                        {t('tenantSignup.subdomainTaken')}
                      </p>
                    )}
                  </div>

                  <Separator className="my-4" />

                  {/* Branding Section */}
                  <h3 className="font-semibold flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    {t('tenantSignup.branding', 'Brand Customization')}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t(
                      'tenantSignup.brandingDesc',
                      "Customize your gym's look and feel"
                    )}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Brand Color */}
                    <div>
                      <Label htmlFor="brandColor">
                        {t('tenantSignup.brandColor', 'Brand Color')}
                      </Label>
                      <div className="flex items-center gap-3 mt-1">
                        <input
                          type="color"
                          id="brandColor"
                          value={brandColor}
                          onChange={handleBrandColorChange}
                          className="w-12 h-10 cursor-pointer rounded border border-input p-1"
                        />
                        <Input
                          value={brandColor}
                          onChange={(e) => {
                            const color = e.target.value;
                            // Update state - useEffect will apply the preview if valid
                            if (
                              /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color)
                            ) {
                              setBrandColor(color);
                            }
                          }}
                          placeholder="#3B82F6"
                          className="flex-1 font-mono"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t(
                          'tenantSignup.brandColorHint',
                          'This will be your primary accent color'
                        )}
                      </p>
                    </div>

                    {/* Logo Upload */}
                    <div>
                      <Label htmlFor="logo">
                        {t('tenantSignup.logo', 'Logo')}{' '}
                        <span className="text-muted-foreground text-xs">
                          ({t('tenantSignup.optional', 'optional')})
                        </span>
                      </Label>
                      <div className="mt-1">
                        {logoPreview ? (
                          <div className="relative inline-block">
                            <img
                              src={logoPreview}
                              alt="Logo preview"
                              className="h-16 w-auto object-contain rounded border border-input p-1 bg-background"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute -top-2 -right-2 h-6 w-6"
                              onClick={handleRemoveLogo}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <label
                            htmlFor="logoInput"
                            className="flex items-center justify-center gap-2 h-16 px-4 border-2 border-dashed border-input rounded-lg cursor-pointer hover:border-primary transition-colors"
                          >
                            <Upload className="h-5 w-5 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {t('tenantSignup.uploadLogo', 'Upload logo')}
                            </span>
                          </label>
                        )}
                        <input
                          type="file"
                          id="logoInput"
                          accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t(
                          'tenantSignup.logoHint',
                          'PNG, JPG, SVG, WebP or GIF (max 2MB)'
                        )}
                      </p>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  <h3 className="font-semibold">
                    {t('tenantSignup.ownerDetails')}
                  </h3>

                  {/* Owner Name */}
                  <div>
                    <Label htmlFor="ownerName">
                      {t('tenantSignup.ownerName')}
                    </Label>
                    <Input
                      id="ownerName"
                      {...register('ownerName')}
                      placeholder={t('tenantSignup.ownerNamePlaceholder')}
                    />
                    {errors.ownerName && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.ownerName.message}
                      </p>
                    )}
                  </div>

                  {/* Owner Email */}
                  <div>
                    <Label htmlFor="ownerEmail">
                      {t('tenantSignup.ownerEmail')}
                    </Label>
                    <Input
                      id="ownerEmail"
                      type="email"
                      {...register('ownerEmail')}
                      placeholder="owner@yourgym.com"
                    />
                    {errors.ownerEmail && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.ownerEmail.message}
                      </p>
                    )}
                  </div>

                  {/* Password */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ownerPassword">
                        {t('tenantSignup.password')}
                      </Label>
                      <Input
                        id="ownerPassword"
                        type="password"
                        {...register('ownerPassword')}
                      />
                      {errors.ownerPassword && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.ownerPassword.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="confirmPassword">
                        {t('tenantSignup.confirmPassword')}
                      </Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        {...register('confirmPassword')}
                      />
                      {errors.confirmPassword && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.confirmPassword.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <Separator className="my-4" />

                  <h3 className="font-semibold">
                    {t('tenantSignup.selectPlan')}
                  </h3>

                  {/* Number of Gyms */}
                  <div>
                    <Label htmlFor="gymCount">
                      {t('tenantSignup.numberOfGyms')}
                    </Label>
                    <Select
                      value={String(gymCount)}
                      onValueChange={(value) =>
                        setValue('gymCount', parseInt(value))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t('tenantSignup.selectGyms')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 10, 15, 20].map((num) => (
                          <SelectItem key={num} value={String(num)}>
                            {num}{' '}
                            {num === 1
                              ? t('tenantSignup.gym')
                              : t('tenantSignup.gyms')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {isPricingLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  ) : (
                    renderPricingCards()
                  )}

                  <Button
                    type="button"
                    className="w-full hover:opacity-90"
                    style={{ backgroundColor: brandRed }}
                    size="lg"
                    onClick={handleContinueToPayment}
                    disabled={!subdomainAvailable || signupMutation.isPending}
                  >
                    {t('tenantSignup.continueToPayment')}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Summary Sidebar */}
            <div className="space-y-6">
              {/* Branding Preview */}
              <Card className="border-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    {t('tenantSignup.brandingPreview', 'Branding Preview')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo"
                        className="h-10 w-auto object-contain rounded"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium">
                        {watch('businessName') ||
                          t('tenantSignup.yourGym', 'Your Gym')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {watch('subdomain') || 'yourgym'}.thenotfit.com
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {t('tenantSignup.primaryColor', 'Primary Color')}:
                    </span>
                    <div
                      className="w-6 h-6 rounded border"
                      style={{ backgroundColor: brandColor }}
                    />
                    <span className="text-sm font-mono">{brandColor}</span>
                  </div>
                  <Button 
                    className="w-full hover:opacity-90" 
                    style={{ backgroundColor: brandColor }}
                    size="sm"
                  >
                    {t('tenantSignup.sampleButton', 'Sample Button')}
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t('tenantSignup.orderSummary')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('tenantSignup.plan')}
                    </span>
                    <span className="font-medium">
                      {selectedPlan?.name || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('tenantSignup.gyms')}
                    </span>
                    <span className="font-medium">{gymCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('tenantSignup.pricePerGym')}
                    </span>
                    <span className="font-medium">
                      €{pricingData?.pricePerGym || 100}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-lg font-bold">
                    <span>{t('tenantSignup.total')}</span>
                    <span>
                      €{totalPrice}/{t('tenantSignup.month')}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="pt-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-green-500" />
                    <span className="text-sm">
                      {t('tenantSignup.securePayment')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-blue-500" />
                    <span className="text-sm">
                      {t('tenantSignup.instantSetup')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-purple-500" />
                    <span className="text-sm">
                      {t('tenantSignup.dedicatedDatabase')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe className="h-5 w-5 text-orange-500" />
                    <span className="text-sm">
                      {t('tenantSignup.customSubdomain')}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 2: Confirm & Pay */}
        {step === 2 && (
          <Card className="max-w-2xl mx-auto border-2">
            <CardHeader>
              <CardTitle>{t('tenantSignup.confirmAndPay')}</CardTitle>
              <CardDescription>
                {t('tenantSignup.reviewDetails')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Summary */}
              <div className="bg-muted p-4 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span>{t('tenantSignup.businessName')}</span>
                  <span className="font-medium">{watch('businessName')}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('tenantSignup.subdomain')}</span>
                  <span className="font-medium">
                    {watch('subdomain')}.thenotfit.com
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('tenantSignup.ownerEmail')}</span>
                  <span className="font-medium">{watch('ownerEmail')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>{t('tenantSignup.brandColor', 'Brand Color')}</span>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-5 h-5 rounded border"
                      style={{ backgroundColor: brandColor }}
                    />
                    <span className="font-medium font-mono text-sm">
                      {brandColor}
                    </span>
                  </div>
                </div>
                {logoPreview && (
                  <div className="flex justify-between items-center">
                    <span>{t('tenantSignup.logo', 'Logo')}</span>
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="h-8 w-auto object-contain rounded"
                    />
                  </div>
                )}
                <div className="flex justify-between">
                  <span>{t('tenantSignup.plan')}</span>
                  <Badge>{selectedPlan?.name}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>{t('tenantSignup.gyms')}</span>
                  <span className="font-medium">{gymCount}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>{t('tenantSignup.monthlyTotal')}</span>
                  <span>€{totalPrice}</span>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  {t('tenantSignup.back')}
                </Button>
                <Button
                  className="flex-1 hover:opacity-90"
                  style={{ backgroundColor: brandRed }}
                  onClick={handleSubmit(onSubmit)}
                  disabled={signupMutation.isPending}
                >
                  {signupMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('tenantSignup.processing')}
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      {t('tenantSignup.proceedToPayment')}
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground">
                {t('tenantSignup.stripeRedirect')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Login link */}
        <p className="text-center mt-8 text-muted-foreground">
          {t('tenantSignup.alreadyHaveAccount')}{' '}
          <Link 
            to="/login" 
            className="font-medium hover:underline"
            style={{ color: brandRed }}
          >
            {t('tenantSignup.signIn')}
          </Link>
        </p>
      </div>
    </div>
  );
}
